import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, UserPlus, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setError('');
    setSuccess('');
  };

  const handleModeSwitch = () => {
    resetForm();
    setIsRegisterMode(!isRegisterMode);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }
    
    if (password.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens bevatten');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        name,
        role: 'employee'
      });
      
      if (response.data.requiresVerification) {
        setSuccess('Account succesvol aangemaakt! Controleer uw e-mail voor een verificatielink. Na verificatie wacht uw account op goedkeuring door de beheerder.');
      } else if (response.data.requiresApproval) {
        setSuccess('Account succesvol aangemaakt! Je registratie wacht op goedkeuring van de eigenaar. Je kunt inloggen zodra deze is goedgekeurd.');
      } else {
        setSuccess('Account succesvol aangemaakt! Je kunt nu inloggen.');
        setTimeout(() => {
          setIsRegisterMode(false);
          setPassword('');
          setConfirmPassword('');
          setName('');
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Account aanmaken mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="space-y-4 pb-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold text-white">VS</span>
            </div>
          </div>
          <CardTitle className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Van Vessem</h1>
            <p className="text-sm text-gray-500 mt-2">Sound & Lights</p>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => !isRegisterMode || handleModeSwitch()}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                !isRegisterMode 
                  ? 'bg-white shadow text-gray-900' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Inloggen
            </button>
            <button
              type="button"
              onClick={() => isRegisterMode || handleModeSwitch()}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                isRegisterMode 
                  ? 'bg-white shadow text-gray-900' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Registreren
            </button>
          </div>

          <form onSubmit={isRegisterMode ? handleRegister : handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            {isRegisterMode && (
              <div className="space-y-2">
                <Label htmlFor="name">Volledige Naam</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Jan Jansen"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                placeholder={isRegisterMode ? "jouw.email@voorbeeld.nl" : "info@vanvessemsoundlights.nl"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                data-testid="password-input"
              />
            </div>

            {isRegisterMode && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Wachtwoord Bevestigen</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="confirm-password-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg text-base"
              data-testid={isRegisterMode ? "register-button" : "login-button"}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isRegisterMode ? 'Account aanmaken...' : 'Inloggen...'}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  {isRegisterMode ? (
                    <>
                      <UserPlus className="w-5 h-5" />
                      <span>Account Aanmaken</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      <span>Inloggen</span>
                    </>
                  )}
                </div>
              )}
            </Button>
          </form>

          {isRegisterMode && (
            <div className="mt-4 text-center text-sm text-gray-500">
              <p>Door te registreren word je toegevoegd als personeelslid.</p>
              <p className="mt-1">Neem contact op met de eigenaar voor extra rechten.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
